import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Shield, RefreshCw, ChevronDown, ChevronUp, CheckCircle, Play, Gauge, Settings, LogOut, ShieldOff, Clock, AlertTriangle, TrendingDown
} from "lucide-react";

const stateColors = { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" };
const urgColors = { BUY_NOW: "#10B981", URGENT: "#EF4444", RECOMMENDED: "#F59E0B", MONITOR: "var(--hoip-text-tertiary)" };

export default function HedgingPage() {
  const navigate = useNavigate();
  const [hedges, setHedges] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState({});
  const [selectedStrat, setSelectedStrat] = useState({});
  const [hedgeRegimes, setHedgeRegimes] = useState({});
  const [expandedTicker, setExpandedTicker] = useState(null);
  const [floorPct, setFloorPct] = useState(85);
  const [floorEditing, setFloorEditing] = useState(false);
  const [floorInput, setFloorInput] = useState("85");
  const [dteMode, setDteMode] = useState("traditional");
  const [stressData, setStressData] = useState(null);
  const [stressLoading, setStressLoading] = useState(false);

  // Auto-load on mount
  useEffect(() => {
    api.get("/portfolio/summary").then(({ data }) => {
      const fp = data?.floor_pct || 85;
      setFloorPct(fp);
      setFloorInput(String(fp));
    }).catch(() => {});
    load("traditional");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (mode) => {
    const m = mode || dteMode;
    setLoading(true);
    try {
      const { data } = await api.get(`/portfolio/hedging-program?dte_mode=${m}`);
      setHedges(data);
      setSelectedStrat({}); // Reset selections on reload
      const tickers = [...new Set((data.hedges || []).map(h => h.ticker))];
      tickers.forEach(t => {
        api.get(`/regime/${t}`).then(({ data: rd }) => {
          setHedgeRegimes(prev => ({ ...prev, [t]: rd }));
        }).catch(() => {});
      });
    }
    catch (e) { console.warn(e); }
    finally { setLoading(false); }
  };

  const saveFloor = async () => {
    const val = parseFloat(floorInput);
    if (val > 0 && val <= 100) {
      await api.put("/portfolio/settings", { floor_pct: val });
      setFloorPct(val);
      setFloorEditing(false);
      load(dteMode);
    }
  };

  const switchDteMode = (mode) => {
    setDteMode(mode);
    load(mode);
  };

  // Build hedge payload for post-hedge stress test
  const buildHedgePayload = () => {
    const payload = {};
    (hedges?.hedges || []).forEach(h => {
      const stratIdx = selectedStrat[h.ticker];
      if (stratIdx === undefined || stratIdx === null) return;
      if (stratIdx === -1) {
        payload[h.ticker] = { action: "divest" };
      } else if (stratIdx === -2) {
        payload[h.ticker] = { action: "no_hedge" };
      } else {
        const strat = h.strategies?.[stratIdx] || h.strategies?.[0];
        payload[h.ticker] = {
          action: strat?.type === "collar" ? "collar" : "put",
          put_strike: strat?.put_strike || h.floor_strike,
        };
      }
    });
    return payload;
  };

  // Fetch post-hedge stress test when selections change
  useEffect(() => {
    if (!hasAnySelection || !hedges) return;
    const payload = buildHedgePayload();
    if (Object.keys(payload).length === 0) return;
    setStressLoading(true);
    api.post("/portfolio/risk-analysis/post-hedge", { hedges: payload })
      .then(({ data }) => setStressData(data))
      .catch(() => {})
      .finally(() => setStressLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStrat, hedges]);

  const executeHedge = async (h, stratIdx) => {
    const strat = h.strategies[stratIdx];
    const key = `${h.ticker}-${stratIdx}`;
    setExecuting(p => ({ ...p, [key]: true }));
    try {
      await api.post("/portfolio/execute-hedge", {
        strategy_type: strat.type, ticker: h.ticker,
        put_strike: strat.put_strike, call_strike: strat.call_strike,
        contracts: h.contracts_needed, net_cost: strat.net_cost,
        expiry: h.expiry, dte: h.dte,
      });
    } catch (e) { console.warn(e); }
    finally { setExecuting(p => ({ ...p, [key]: "done" })); }
  };

  // Dynamic total hedge cost based on selected strategies (only hedged positions)
  const dynamicTotalCost = (hedges?.hedges || []).reduce((sum, h) => {
    const stratIdx = selectedStrat[h.ticker];
    if (stratIdx === undefined || stratIdx === null || stratIdx === -1 || stratIdx === -2) return sum;
    const strat = h.strategies?.[stratIdx] || h.strategies?.[0];
    return sum + (strat?.net_cost || 0);
  }, 0);

  const preHedgeDD = hedges?.summary?.max_drawdown_allowed || 0;

  // Post-hedge drawdown: max portfolio loss with selected hedges active
  const positionDrawdowns = (hedges?.hedges || []).map(h => {
    const stratIdx = selectedStrat[h.ticker];
    // Not selected or No Hedge: full proportional exposure
    if (stratIdx === undefined || stratIdx === null || stratIdx === -2) {
      return { ticker: h.ticker, maxLoss: Math.round(preHedgeDD * (h.weight_pct / 100)), noHedge: true };
    }
    // Divest: zero risk
    if (stratIdx === -1) return { ticker: h.ticker, maxLoss: 0, divested: true };
    // Hedged: capped at put strike
    const strat = h.strategies?.[stratIdx] || h.strategies?.[0];
    const putStrike = strat?.put_strike || h.floor_strike;
    const coveredShares = Math.min(h.shares, (h.contracts_needed || 1) * 100);
    const uncoveredShares = Math.max(0, h.shares - coveredShares);
    const hedgedLoss = Math.max(0, (h.current_price - putStrike) * coveredShares);
    const unhedgedLoss = h.current_price * uncoveredShares;
    return { ticker: h.ticker, maxLoss: hedgedLoss + unhedgedLoss, divested: false, putStrike, coveredShares, uncoveredShares };
  });
  const postHedgeDrawdown = positionDrawdowns.reduce((s, p) => s + p.maxLoss, 0) + dynamicTotalCost;
  const ddReduction = preHedgeDD - Math.max(0, postHedgeDrawdown);
  const ddReductionPct = preHedgeDD > 0 ? (ddReduction / preHedgeDD) * 100 : 0;
  const isOverExposed = postHedgeDrawdown > preHedgeDD;
  const hasAnySelection = Object.keys(selectedStrat).length > 0;

  const sm = hedges?.summary;

  // RECOMMENDED tag logic per position
  const getRecommended = (h) => {
    const rgu = regimeUrgency(h.ticker);
    const isBenign = rgu?.label === "LOW RISK" || rgu?.label === "MONITOR";
    const severeLoss = (h.pnl_pct || 0) < -40;
    const moderateLoss = (h.pnl_pct || 0) < -30;
    const nlv = sm?.nlv || 100000;

    if (severeLoss && isBenign) return { idx: -1, label: "RECOMMENDED", reason: "Severe loss — consider exiting" };

    // Evaluate hedge strategies
    let bestIdx = null;
    let bestEff = 0;
    (h.strategies || []).forEach((s, si) => {
      const premium = Math.abs(s.net_cost || 0);
      if (premium <= 0) return;
      const exposedLoss = (h.current_price || 0) * (h.shares || 0) * 0.15;
      const hedgedLoss = Math.max(0, ((h.current_price || 0) - (s.put_strike || h.floor_strike)) * Math.min(h.shares, (h.contracts_needed || 1) * 100));
      const avoided = exposedLoss - hedgedLoss;
      const eff = avoided / premium;
      if (eff > bestEff) { bestEff = eff; bestIdx = si; }
    });

    if (bestIdx !== null && bestEff > 2.0) {
      const bestStrat = h.strategies[bestIdx];
      const premium = Math.abs(bestStrat?.net_cost || 0);
      if (premium < nlv * 0.03) return { idx: bestIdx, label: "RECOMMENDED", reason: `Efficiency ${bestEff.toFixed(1)}x` };
    }

    if (isBenign && !moderateLoss) return { idx: -2, label: "RECOMMENDED", reason: "Benign regime — monitor" };
    return null;
  };

  // Hedge efficiency for a specific strategy
  const hedgeEfficiency = (h, strat) => {
    const premium = Math.abs(strat?.net_cost || 0);
    if (premium <= 0) return null;
    const putStrike = strat?.put_strike || h.floor_strike;
    const coveredShares = Math.min(h.shares, (h.contracts_needed || 1) * 100);
    const exposedLoss = (h.current_price || 0) * coveredShares * 0.15;
    const hedgedLoss = Math.max(0, ((h.current_price || 0) - putStrike) * coveredShares);
    const avoided = exposedLoss - hedgedLoss;
    return avoided > 0 ? (avoided / premium) : 0;
  };

  const regimeUrgency = (ticker) => {
    const rg = hedgeRegimes[ticker];
    if (!rg) return null;
    const state = rg.dominant_state_label;
    const sojourn = rg.expected_sojourn_days;
    const prob = rg.dominant_prob;
    if ((state === "Bearish Trend" || state === "Volatile") && prob > 0.6)
      return { label: "ACTION ADVISED", color: "#EF4444", detail: `${state} (${sojourn?.toFixed(0)}d sojourn)` };
    if (state === "Volatile" && prob > 0.4)
      return { label: "ELEVATED", color: "#F59E0B", detail: `Vol regime ${(prob*100).toFixed(0)}% (${sojourn?.toFixed(0)}d)` };
    if (state === "Quiet Range" || state === "Bullish Trend")
      return { label: "LOW RISK", color: "#10B981", detail: `${state} (${sojourn?.toFixed(0)}d sojourn)` };
    return { label: "MONITOR", color: "var(--hoip-text-tertiary)", detail: state };
  };

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4" data-testid="hedging-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-red-400" />
          <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Hedging Program</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* DTE Mode Toggle */}
          <div className="flex items-center gap-1 border rounded-sm px-1" style={{ borderColor: "var(--hoip-border)" }} data-testid="dte-mode-toggle">
            <Clock className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} />
            <button onClick={() => switchDteMode("traditional")}
              className={`px-2 py-1 text-[9px] rounded-sm transition-colors ${dteMode === "traditional" ? "bg-blue-600 text-white" : "text-[var(--hoip-text-tertiary)] hover:text-[var(--hoip-text-secondary)]"}`}
              data-testid="dte-traditional-btn"
              title="Standard portfolio management: ~90 day hedge duration">
              Traditional
            </button>
            <button onClick={() => switchDteMode("regime")}
              className={`px-2 py-1 text-[9px] rounded-sm transition-colors ${dteMode === "regime" ? "bg-amber-600 text-white" : "text-[var(--hoip-text-tertiary)] hover:text-[var(--hoip-text-secondary)]"}`}
              data-testid="dte-regime-btn"
              title="Match hedge duration to HMM regime sojourn time">
              Regime
            </button>
          </div>
          <Button size="sm" onClick={() => load(dteMode)} disabled={loading} className="h-8 bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs px-4" data-testid="hedging-analyze-btn">
            {loading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />} Refresh
          </Button>
        </div>
      </div>

      {/* DTE Mode explanation */}
      {hedges && (
        <div className="text-[9px] px-1" style={{ color: "var(--hoip-text-tertiary)" }}>
          {dteMode === "traditional"
            ? "Traditional: ~90d DTE — standard portfolio management practice for protective hedges."
            : "Regime: P75 sojourn + 15% buffer — covers tail risk, gamma exposure near expiry, and HMM estimation error."}
        </div>
      )}

      {/* Summary Metrics */}
      {sm && (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>NLV</span>
          <span className="font-mono-data text-sm font-medium">${sm.nlv?.toLocaleString()}</span>
        </div>
        <div className="border p-3 cursor-pointer" style={{ background: "var(--hoip-card)", borderColor: floorEditing ? "#3B82F6" : "var(--hoip-border)" }} onClick={() => { if (!floorEditing) setFloorEditing(true); }} data-testid="floor-edit-card">
          <div className="flex items-center justify-between">
            <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Floor ({floorPct}% of peak)</span>
            <Settings className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} />
          </div>
          {floorEditing ? (
            <div className="flex items-center gap-1 mt-0.5">
              <Input type="number" value={floorInput} onChange={e => setFloorInput(e.target.value)} className="h-6 w-14 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)]" style={{ color: "var(--hoip-text-primary)" }} data-testid="floor-input" autoFocus onKeyDown={e => { if (e.key === "Enter") saveFloor(); }} />
              <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>%</span>
              <button onClick={saveFloor} className="text-[9px] px-1.5 py-0.5 bg-blue-600 text-white rounded-sm">Save</button>
              <button onClick={() => setFloorEditing(false)} className="text-[9px] px-1 text-red-400">X</button>
            </div>
          ) : (
            <span className="font-mono-data text-sm font-medium">${sm.effective_floor?.toLocaleString()}</span>
          )}
        </div>
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Max Drawdown</span>
          <span className="font-mono-data text-sm font-medium text-red-400">${sm.max_drawdown_allowed?.toLocaleString()}</span>
        </div>
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Total Hedge Cost (selected)</span>
          <span className="font-mono-data text-sm font-medium" style={{ color: dynamicTotalCost <= 0 ? "#10B981" : "#EF4444" }}>
            {dynamicTotalCost <= 0 ? `+$${Math.abs(dynamicTotalCost).toLocaleString()}` : `$${dynamicTotalCost.toLocaleString()}`}
          </span>
        </div>
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Hedge % NLV</span>
          <span className="font-mono-data text-sm font-medium">{sm.nlv ? (Math.abs(dynamicTotalCost) / sm.nlv * 100).toFixed(2) : "0"}%</span>
        </div>
      </div>
      )}

      {/* Drawdown Analysis — Before vs After Hedging */}
      {sm && hedges?.hedges?.length > 0 && (
        <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="drawdown-analysis">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-medium tracking-wide uppercase" style={{ color: "var(--hoip-text-tertiary)" }}>Drawdown Analysis</span>
            {preHedgeDD > 0 && hasAnySelection && (
              <span className="text-[10px] px-2 py-0.5 rounded-sm font-bold" style={{
                color: postHedgeDrawdown <= 0 ? "#10B981" : ddReductionPct > 50 ? "#10B981" : ddReductionPct > 0 ? "#F59E0B" : "#EF4444",
                background: postHedgeDrawdown <= 0 ? "rgba(16,185,129,0.1)" : ddReductionPct > 50 ? "rgba(16,185,129,0.1)" : ddReductionPct > 0 ? "rgba(245,158,11,0.1)" : "rgba(239,68,68,0.1)"
              }} data-testid="dd-reduction-badge">
                {postHedgeDrawdown <= 0 ? "FULLY PROTECTED" : isOverExposed ? `${Math.abs(ddReductionPct).toFixed(0)}% above floor` : `${ddReductionPct.toFixed(0)}% protection`}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between text-[10px] mb-1">
                <span style={{ color: "var(--hoip-text-tertiary)" }}>Before Hedging</span>
                <span className="font-mono-data text-red-400 font-medium" data-testid="dd-before-value">${preHedgeDD.toLocaleString()}</span>
              </div>
              <div className="h-3 rounded-sm overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
                <div className="h-full rounded-sm transition-all duration-300" style={{ width: `${isOverExposed ? Math.round(preHedgeDD / postHedgeDrawdown * 100) : 100}%`, background: "linear-gradient(90deg, #EF4444 0%, #DC2626 100%)" }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-[10px] mb-1">
                <span style={{ color: "var(--hoip-text-tertiary)" }}>After Hedging</span>
                {hasAnySelection ? (
                  <span className="font-mono-data font-medium" style={{ color: postHedgeDrawdown <= 0 ? "#10B981" : isOverExposed ? "#EF4444" : "#F59E0B" }} data-testid="dd-after-value">
                    {postHedgeDrawdown <= 0 ? `+$${Math.abs(postHedgeDrawdown).toLocaleString()}` : `$${Math.max(0, postHedgeDrawdown).toLocaleString()}`}
                  </span>
                ) : (
                  <span className="font-mono-data text-red-400 font-medium" data-testid="dd-after-value">${preHedgeDD.toLocaleString()}</span>
                )}
              </div>
              <div className="h-3 rounded-sm overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
                {hasAnySelection ? (
                  <div className="h-full rounded-sm transition-all duration-500" style={{
                    width: `${postHedgeDrawdown <= 0 ? 2 : isOverExposed ? 100 : Math.max(2, (postHedgeDrawdown / preHedgeDD) * 100)}%`,
                    background: postHedgeDrawdown <= 0 ? "#10B981" : isOverExposed ? "linear-gradient(90deg, #EF4444 0%, #B91C1C 100%)" : "linear-gradient(90deg, #F59E0B 0%, #EAB308 100%)"
                  }} />
                ) : (
                  <div className="h-full rounded-sm" style={{ width: "100%", background: "linear-gradient(90deg, #EF4444 0%, #DC2626 100%)" }} />
                )}
              </div>
              {!hasAnySelection && <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Select a hedge action below to see impact</div>}
            </div>
          </div>
          {/* Per-position breakdown */}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            {positionDrawdowns.map(pd => (
              <span key={pd.ticker} className="text-[9px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
                <span className="text-blue-400">{pd.ticker}</span>{" "}
                {pd.divested ? (
                  <span className="text-red-400">DIVEST</span>
                ) : pd.noHedge ? (
                  <span style={{ color: "var(--hoip-text-tertiary)" }}>${pd.maxLoss.toLocaleString()} (exposed)</span>
                ) : (
                  <span style={{ color: pd.maxLoss === 0 ? "#10B981" : "var(--hoip-text-secondary)" }}>${pd.maxLoss.toLocaleString()}</span>
                )}
              </span>
            ))}
            {dynamicTotalCost !== 0 && (
              <span className="text-[9px] font-mono-data" style={{ color: dynamicTotalCost <= 0 ? "#10B981" : "#EF4444" }}>
                Net: {dynamicTotalCost <= 0 ? `+$${Math.abs(dynamicTotalCost).toLocaleString()}` : `-$${dynamicTotalCost.toLocaleString()}`}
              </span>
            )}
          </div>
          {isOverExposed && hasAnySelection && (
            <div className="mt-2 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
              Switch to collars to receive premium and reduce post-hedge drawdown below floor.
            </div>
          )}
          {!hasAnySelection && (
            <div className="mt-2 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
              Expand each position and select a strategy to see how your drawdown changes.
            </div>
          )}
        </div>
      )}

      {/* Post-Hedge Stress Test — Before vs After */}
      {hasAnySelection && stressData && (
        <div className="border overflow-hidden" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="post-hedge-stress">
          <div className="px-4 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--hoip-border)" }}>
            <div className="flex items-center gap-2">
              <TrendingDown className="w-3.5 h-3.5 text-red-400" />
              <span className="text-[10px] font-medium tracking-wide uppercase" style={{ color: "var(--hoip-text-tertiary)" }}>Stress Test — Before vs After Hedging</span>
            </div>
            {stressLoading && <RefreshCw className="w-3 h-3 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />}
          </div>
          <table className="w-full text-[10px]" data-testid="stress-comparison-table">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                <th className="text-left px-3 py-1.5 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Scenario</th>
                <th className="text-right px-2 py-1.5 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Before NLV</th>
                <th className="text-center px-2 py-1.5 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}></th>
                <th className="text-right px-2 py-1.5 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>After NLV</th>
                <th className="text-right px-2 py-1.5 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Saved</th>
                <th className="text-center px-3 py-1.5 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Floor</th>
              </tr>
            </thead>
            <tbody>
              {(stressData.before || []).map((bsc, i) => {
                const asc = stressData.after?.[i];
                if (!asc) return null;
                const saved = asc.nlv - bsc.nlv;
                const floorFixed = bsc.breach && !asc.breach;
                const floorLine = stressData.effective_floor;
                return (
                  <tr key={i} className="border-b" style={{
                    borderColor: "var(--hoip-divider)",
                    background: floorFixed ? "rgba(16,185,129,0.04)" : bsc.breach ? "rgba(239,68,68,0.04)" : undefined
                  }}>
                    <td className="px-3 py-1.5 font-mono-data font-medium">{bsc.label} <span style={{ color: "var(--hoip-text-tertiary)" }}>({bsc.pct}%)</span></td>
                    <td className="text-right px-2 py-1.5 font-mono-data" style={{ color: bsc.breach ? "#EF4444" : "var(--hoip-text-secondary)" }}>
                      ${bsc.nlv?.toLocaleString()}
                    </td>
                    <td className="text-center px-1 py-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>→</td>
                    <td className="text-right px-2 py-1.5 font-mono-data font-medium" style={{ color: asc.breach ? "#EF4444" : "#10B981" }}>
                      ${asc.nlv?.toLocaleString()}
                    </td>
                    <td className="text-right px-2 py-1.5 font-mono-data" style={{ color: saved > 0 ? "#10B981" : saved < 0 ? "#EF4444" : "var(--hoip-text-tertiary)" }}>
                      {saved > 0 ? `+$${saved.toLocaleString()}` : saved < 0 ? `-$${Math.abs(saved).toLocaleString()}` : "—"}
                    </td>
                    <td className="text-center px-3 py-1.5">
                      {floorFixed ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold bg-emerald-900/20 text-emerald-400">FIXED</span>
                      ) : asc.breach ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold bg-red-900/20 text-red-400">BREACH</span>
                      ) : (
                        <span className="text-emerald-500 text-[9px]">OK</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Summary line */}
          {(() => {
            const breachesBefore = (stressData.before || []).filter(s => s.breach).length;
            const breachesAfter = (stressData.after || []).filter(s => s.breach).length;
            const fixed = breachesBefore - breachesAfter;
            return (
              <div className="px-4 py-2 flex items-center justify-between text-[9px]" style={{ background: "rgba(255,255,255,0.01)" }}>
                <span style={{ color: "var(--hoip-text-tertiary)" }}>
                  Floor: ${stressData.effective_floor?.toLocaleString()}
                </span>
                <span style={{ color: fixed > 0 ? "#10B981" : breachesAfter > 0 ? "#F59E0B" : "var(--hoip-text-tertiary)" }}>
                  {fixed > 0 && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                  {breachesAfter === 0
                    ? "All scenarios protected"
                    : fixed > 0
                      ? `${fixed} breach${fixed > 1 ? "es" : ""} fixed, ${breachesAfter} remaining`
                      : `${breachesAfter} scenario${breachesAfter > 1 ? "s" : ""} still breach floor`}
                </span>
              </div>
            );
          })()}
        </div>
      )}

      {/* Loading state */}
      {loading && !hedges && (
        <div className="border p-8 flex items-center justify-center" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <RefreshCw className="w-5 h-5 animate-spin mr-2" style={{ color: "var(--hoip-text-tertiary)" }} />
          <span className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>Analyzing hedges with live option chains...</span>
        </div>
      )}

      {/* Summary Table */}
      {hedges?.hedges?.length > 0 && (
        <div className="border overflow-x-auto" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <table className="w-full dense-table" data-testid="hedge-summary-table">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Shares</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>P&L%</th>
                <th className="text-center text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Risk Mgmt</th>
                <th className="text-center text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Regime View</th>
                <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Action</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Net Cost</th>
                <th className="text-center text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {hedges.hedges.map((h, i) => {
                const activeStrat = selectedStrat[h.ticker];
                const isNotSelected = activeStrat === undefined || activeStrat === null;
                const isDivest = activeStrat === -1;
                const isNoHedge = activeStrat === -2;
                const strat = (isDivest || isNoHedge || isNotSelected) ? null : (h.strategies?.[activeStrat] || h.strategies?.[0]);
                const rgu = regimeUrgency(h.ticker);
                const isExpanded = expandedTicker === h.ticker;
                return (
                <React.Fragment key={`hedge-${h.ticker}`}>
                <tr className={`border-b cursor-pointer hover:bg-[#1A1A1A] ${isExpanded ? "bg-[#1A1A1A]" : ""}`} style={{ borderColor: "var(--hoip-divider)" }}
                  onClick={() => setExpandedTicker(isExpanded ? null : h.ticker)} data-testid={`hedge-row-${h.ticker}`}>
                  <td className="font-mono-data text-[10px] font-medium">
                    <span className="text-blue-400 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); navigate(`/ticker/${h.ticker}`); }}>{h.ticker}</span>
                    <span className="text-[9px] ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>{h.weight_pct}%</span>
                  </td>
                  <td className="text-right font-mono-data text-[10px]">{h.shares?.toLocaleString()}</td>
                  <td className={`text-right font-mono-data text-[10px] ${h.pnl_pct >= 0 ? "text-emerald-500" : "text-red-400"}`}>{h.pnl_pct >= 0 ? "+" : ""}{h.pnl_pct}%</td>
                  <td className="text-center">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold" style={{ color: urgColors[h.urgency], background: `${urgColors[h.urgency]}15` }}>
                      {h.urgency?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="text-center">
                    {rgu ? (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold" style={{ color: rgu.color, background: `${rgu.color}15` }} title={rgu.detail}>
                        {rgu.label}
                      </span>
                    ) : <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>...</span>}
                  </td>
                  <td className="text-[10px]" onClick={(e) => e.stopPropagation()}>
                    {isNotSelected ? (
                      <span style={{ color: "var(--hoip-text-tertiary)", fontStyle: "italic" }}>— Select —</span>
                    ) : isDivest ? (
                      <span className="text-red-400 font-bold">DIVEST</span>
                    ) : isNoHedge ? (
                      <span style={{ color: "var(--hoip-text-tertiary)" }}>No Hedge</span>
                    ) : (
                      <span style={{ color: "var(--hoip-text-secondary)" }}>
                        {strat?.label || "—"}
                        {h.strategies?.length > 1 && <span className="text-[9px] ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>({h.strategies.length + 2})</span>}
                      </span>
                    )}
                  </td>
                  <td className="text-right font-mono-data text-[10px]">
                    {(isNotSelected || isDivest || isNoHedge) ? (
                      <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>
                    ) : (
                      <span style={{ color: (strat?.net_cost || 0) <= 0 ? "#10B981" : "#EF4444" }}>
                        {(strat?.net_cost || 0) <= 0 ? `+$${Math.abs(strat?.net_cost || 0).toLocaleString()}` : `$${(strat?.net_cost || 0).toLocaleString()}`}
                      </span>
                    )}
                  </td>
                  <td className="text-center">
                    {isExpanded ? <ChevronUp className="w-3 h-3 inline" style={{ color: "var(--hoip-text-tertiary)" }} /> : <ChevronDown className="w-3 h-3 inline" style={{ color: "var(--hoip-text-tertiary)" }} />}
                  </td>
                </tr>

                {/* Expanded Detail */}
                {isExpanded && (
                <tr><td colSpan={8} className="p-0 border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                  <div className="p-3 space-y-2" style={{ background: "rgba(255,255,255,0.02)" }}>
                    {/* Contradiction text: severe loss + benign regime */}
                    {(() => {
                      const rgu = regimeUrgency(h.ticker);
                      const isBenign = rgu?.label === "LOW RISK";
                      const severeLoss = (h.pnl_pct || 0) < -30;
                      if (isBenign && severeLoss) return (
                        <div className="text-[10px] px-3 py-2 border-l-4 rounded-sm" style={{ borderColor: "#F59E0B", background: "rgba(245,158,11,0.06)", color: "var(--hoip-text-secondary)" }}
                          data-testid={`contradiction-${h.ticker}`}>
                          Current regime risk is low — further deterioration from market conditions is unlikely. However, existing loss is already severe ({h.pnl_pct}%). Defensive hedging has limited additional value at this stage. Consider divest or hold depending on conviction.
                        </div>
                      );
                      return null;
                    })()}

                    {/* Regime info */}
                    {hedgeRegimes[h.ticker] && (
                      <div className="flex items-center gap-2 text-[10px]">
                        <Gauge className="w-3 h-3" style={{ color: stateColors[hedgeRegimes[h.ticker].dominant_state_label] }} />
                        <span className="px-1.5 py-0.5 rounded-sm font-bold text-[9px]" style={{ color: stateColors[hedgeRegimes[h.ticker].dominant_state_label], background: `${stateColors[hedgeRegimes[h.ticker].dominant_state_label]}15` }}>
                          {hedgeRegimes[h.ticker].dominant_state_label} {(hedgeRegimes[h.ticker].dominant_prob * 100).toFixed(0)}%
                        </span>
                        <span style={{ color: "var(--hoip-text-tertiary)" }}>Sojourn: {hedgeRegimes[h.ticker].expected_sojourn_days?.toFixed(0)}d</span>
                        <span style={{ color: "var(--hoip-text-tertiary)" }}>Entropy: {hedgeRegimes[h.ticker].regime_entropy?.toFixed(2)}</span>
                      </div>
                    )}

                    <p className="text-[10px]" style={{ color: urgColors[h.urgency] }}>{h.urgency_reason}</p>

                    {/* Strategy Toggle with RECOMMENDED tags */}
                    {(() => {
                      const rec = getRecommended(h);
                      return (
                    <div className="flex gap-1 flex-wrap" data-testid={`hedge-strategy-toggle-${h.ticker}`}>
                      {(h.strategies || []).map((s, si) => (
                        <button key={si} onClick={() => setSelectedStrat(p => ({ ...p, [h.ticker]: si }))}
                          className={`px-2 py-0.5 text-[10px] border rounded-sm ${selectedStrat[h.ticker] === si ? "bg-[var(--hoip-text-primary)] text-[var(--hoip-bg)] border-[var(--hoip-text-primary)]" : "border-[var(--hoip-border)] text-[var(--hoip-text-secondary)]"}`}>
                          {s.label}
                          {rec?.idx === si && <span className="ml-1 text-[8px] px-1 py-px rounded-sm bg-emerald-600 text-white font-bold">REC</span>}
                        </button>
                      ))}
                      <button onClick={() => setSelectedStrat(p => ({ ...p, [h.ticker]: -2 }))}
                        className={`px-2 py-0.5 text-[10px] border rounded-sm flex items-center gap-1 ${selectedStrat[h.ticker] === -2 ? "bg-[var(--hoip-text-primary)] text-[var(--hoip-bg)] border-[var(--hoip-text-primary)]" : "border-[var(--hoip-border)] text-[var(--hoip-text-tertiary)]"}`}
                        data-testid={`hedge-nohedge-${h.ticker}`}>
                        <ShieldOff className="w-2.5 h-2.5" /> No Hedge
                        {rec?.idx === -2 && <span className="ml-1 text-[8px] px-1 py-px rounded-sm bg-emerald-600 text-white font-bold">REC</span>}
                      </button>
                      <button onClick={() => setSelectedStrat(p => ({ ...p, [h.ticker]: -1 }))}
                        className={`px-2 py-0.5 text-[10px] border rounded-sm flex items-center gap-1 ${selectedStrat[h.ticker] === -1 ? "bg-red-600 text-white border-red-600" : "border-red-800 text-red-400"}`}
                        data-testid={`hedge-divest-${h.ticker}`}>
                        <LogOut className="w-2.5 h-2.5" /> Divest
                        {rec?.idx === -1 && <span className="ml-1 text-[8px] px-1 py-px rounded-sm bg-emerald-600 text-white font-bold">REC</span>}
                      </button>
                    </div>
                      );
                    })()}

                    {/* No Hedge info */}
                    {isNoHedge && (
                      <div className="text-[10px] px-2 py-1.5 border rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "rgba(255,255,255,0.02)", color: "var(--hoip-text-tertiary)" }}>
                        Keep position ({h.shares} shares of {h.ticker}), accept full downside risk. Exposure: ${(preHedgeDD * h.weight_pct / 100).toLocaleString()} of max drawdown.
                      </div>
                    )}

                    {/* Divest info */}
                    {isDivest && (
                      <div className="text-[10px] px-2 py-1.5 border rounded-sm" style={{ borderColor: "rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.06)", color: "var(--hoip-text-secondary)" }}>
                        Sell entire position ({h.shares} shares of {h.ticker}). Current value: ${h.market_value?.toLocaleString()} ({h.pnl_pct >= 0 ? "+" : ""}{h.pnl_pct}%).
                      </div>
                    )}

                    {/* Hedge details (only when a hedge strategy is selected) */}
                    {!isDivest && !isNoHedge && !isNotSelected && strat && (
                      <>
                        <div className="text-[10px] px-2 py-1 border rounded-sm" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
                          {strat.description}
                          {strat.call_credit != null && <span className="ml-2 text-emerald-500">Call credit: ${strat.call_credit}</span>}
                          {strat.upside_cap != null && <span className="ml-2" style={{ color: "var(--hoip-text-tertiary)" }}>Upside cap: ${strat.upside_cap?.toLocaleString()}</span>}
                        </div>

                        {/* Hedge Impact Summary */}
                        {(() => {
                          const eff = hedgeEfficiency(h, strat);
                          const premium = Math.abs(strat?.net_cost || 0);
                          const putStrike = strat?.put_strike || h.floor_strike;
                          const coveredShares = Math.min(h.shares, (h.contracts_needed || 1) * 100);
                          const postFloor = putStrike * coveredShares;
                          const pd = positionDrawdowns.find(p => p.ticker === h.ticker);
                          const revisedDD = pd?.maxLoss || 0;
                          return (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] border rounded-sm p-2" style={{ borderColor: "rgba(59,130,246,0.2)", background: "rgba(59,130,246,0.03)" }}
                              data-testid={`hedge-impact-${h.ticker}`}>
                              <div>
                                <span style={{ color: "var(--hoip-text-tertiary)" }}>Est. Premium</span>
                                <div className="font-mono-data font-medium" style={{ color: "#EF4444" }}>${premium.toLocaleString()}</div>
                              </div>
                              <div>
                                <span style={{ color: "var(--hoip-text-tertiary)" }}>Post-Hedge Floor</span>
                                <div className="font-mono-data font-medium">${putStrike} × {coveredShares} = ${postFloor.toLocaleString()}</div>
                              </div>
                              <div>
                                <span style={{ color: "var(--hoip-text-tertiary)" }}>Revised Max DD</span>
                                <div className="font-mono-data font-medium" style={{ color: revisedDD === 0 ? "#10B981" : "#F59E0B" }}>${revisedDD.toLocaleString()}</div>
                              </div>
                              <div>
                                <span style={{ color: "var(--hoip-text-tertiary)" }}>Hedge Efficiency</span>
                                <div className="font-mono-data font-medium" style={{ color: eff > 2 ? "#10B981" : eff > 1 ? "#F59E0B" : "#EF4444" }}>
                                  {eff != null ? `${eff.toFixed(1)}x` : "N/A"}
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[10px]">
                          <div>
                            <span style={{ color: "var(--hoip-text-tertiary)" }}>Put Strike</span>
                            <div className="font-mono-data font-medium">${h.floor_strike} <span className="text-[9px]">({h.pct_otm}% OTM)</span></div>
                          </div>
                          <div>
                            <span style={{ color: "var(--hoip-text-tertiary)" }}>Expiry / DTE</span>
                            <div className="font-mono-data">{h.expiry || "—"} <span className="font-medium">({h.dte || "?"}d)</span></div>
                            {h.regime_sojourn != null && (
                              <div className="text-[8px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
                                Mean: {Math.round(h.regime_sojourn)}d
                                {h.regime_sojourn_p75 != null && <> · P75: {h.regime_sojourn_p75}d</>}
                                {h.regime_sojourn_p90 != null && <> · P90: {h.regime_sojourn_p90}d</>}
                                {h.dte_buffer_pct != null && <> · +{h.dte_buffer_pct}% buf</>}
                                {h.target_dte && <> → target: {h.target_dte}d</>}
                              </div>
                            )}
                            {!h.regime_sojourn && h.target_dte && (
                              <div className="text-[8px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>target: {h.target_dte}d</div>
                            )}
                          </div>
                          <div>
                            <span style={{ color: "var(--hoip-text-tertiary)" }}>Live Price</span>
                            <div className="font-mono-data">{h.live_bid != null ? <span>${h.live_bid} / ${h.live_ask}</span> : <span>~${h.mid_price} est.</span>}</div>
                          </div>
                          <div>
                            <span style={{ color: "var(--hoip-text-tertiary)" }}>Net Cost</span>
                            <div className="font-mono-data font-medium" style={{ color: strat.net_cost <= 0 ? "#10B981" : "#EF4444" }}>
                              {strat.net_cost <= 0 ? `+$${Math.abs(strat.net_cost)}` : `$${strat.net_cost?.toLocaleString()}`}
                              <span className="text-[9px] ml-0.5">(${strat.cost_per_day}/d)</span>
                            </div>
                          </div>
                          <div>
                            <span style={{ color: "var(--hoip-text-tertiary)" }}>Ann. Cost</span>
                            <div className="font-mono-data">{h.annual_cost_pct}% of pos</div>
                          </div>
                        </div>

                        {/* Position drawdown contribution */}
                        {(() => {
                          const pd = positionDrawdowns.find(p => p.ticker === h.ticker);
                          return pd && (
                            <div className="text-[9px] font-mono-data px-2 py-1 border rounded-sm" style={{ borderColor: "rgba(59,130,246,0.2)", background: "rgba(59,130,246,0.04)" }}>
                              <span style={{ color: "var(--hoip-text-tertiary)" }}>Max exposure (hedged): </span>
                              <span className="font-medium" style={{ color: pd.maxLoss === 0 ? "#10B981" : "#F59E0B" }}>${pd.maxLoss.toLocaleString()}</span>
                              <span style={{ color: "var(--hoip-text-tertiary)" }}> — protected below ${(strat?.put_strike || h.floor_strike).toLocaleString()}</span>
                              {pd.uncoveredShares > 0 && <span className="text-red-400 ml-1">({pd.uncoveredShares} uncovered shares)</span>}
                            </div>
                          );
                        })()}

                        {h.live_iv && <div className="text-[9px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>IV: {h.live_iv}% | Tech: {h.tech_score}/10 | RSI: {h.rsi}</div>}

                        <button data-testid={`execute-hedge-${h.ticker}`}
                          disabled={executing[`${h.ticker}-${activeStrat}`]}
                          onClick={() => executeHedge(h, activeStrat)}
                          className={`w-full flex items-center justify-center gap-1.5 py-1.5 text-[10px] border rounded-sm transition-colors ${
                            executing[`${h.ticker}-${activeStrat}`] === "done"
                              ? "bg-emerald-900/20 border-emerald-700 text-emerald-400"
                              : "border-[var(--hoip-border)] text-[var(--hoip-text-secondary)] hover:text-[var(--hoip-text-primary)] hover:border-[var(--hoip-text-secondary)]"
                          }`}>
                          {executing[`${h.ticker}-${activeStrat}`] === "done"
                            ? <><CheckCircle className="w-3 h-3" /> Logged to Trade Journal</>
                            : <><Play className="w-3 h-3" /> Execute {strat.label}</>}
                        </button>
                      </>
                    )}
                  </div>
                </td></tr>
                )}
                </React.Fragment>
              )})}
            </tbody>
          </table>
        </div>
      )}

      {hedges && hedges.hedges?.length === 0 && (
        <div className="border p-8 text-center" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <Shield className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--hoip-text-tertiary)" }} />
          <p className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>No stock positions to hedge.</p>
        </div>
      )}
    </div>
  );
}
